import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
// TODO: Migrate to Better Auth
// import { useAuth } from '@workos-inc/authkit-react'

export const Route = createFileRoute('/_layout/alerts')({
  component: AlertsComponent,
})

const ALERTS_API_URL = 'https://skybit-alerts.cascadexyz.workers.dev'

interface Alert {
  id: string
  user_id: string
  rule_name: string
  min_spread: number
  min_volume: number
  min_confidence_score: number
  platforms: string[]
  max_alerts_per_hour: number
  cooldown_minutes: number
  enabled: boolean
  created_at: number
  updated_at: number
}

interface AlertPreferences {
  rule_name: string
  min_spread: number
  min_volume: number
  min_confidence_score: number
  platforms: string[]
  max_alerts_per_hour: number
  cooldown_minutes: number
  enabled: boolean
}

function AlertsComponent() {
  // TODO: Migrate to Better Auth
  const user = null
  const signIn = () => {}
  const [activeSection, setActiveSection] = useState('manage')
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null)
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [alertPreferences, setAlertPreferences] = useState<AlertPreferences>({
    rule_name: '',
    min_spread: 0.05, // 5% as decimal
    min_volume: 10000,
    min_confidence_score: 0.2, // 20% as decimal
    platforms: ['polymarket', 'kalshi', 'predictit'],
    max_alerts_per_hour: 10,
    cooldown_minutes: 30,
    enabled: true
  })
  const [testStatus, setTestStatus] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user?.id) {
      loadAlerts()
      loadUserPreferences()
    }
  }, [user?.id])

  const loadAlerts = async () => {
    if (!user?.id) return
    
    setLoading(true)
    try {
      const response = await fetch(`${ALERTS_API_URL}/alerts/rules?userId=${user.id}`, {
        headers: {
          'X-User-Id': user.id
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setAlerts(data.rules || [])
      }
    } catch (err) {
      console.error('Failed to load alerts:', err)
      setTestStatus('Failed to load alerts')
    } finally {
      setLoading(false)
    }
  }

  const loadUserPreferences = async () => {
    if (!user?.id) return
    
    try {
      const response = await fetch(`${ALERTS_API_URL}/alerts/preferences?userId=${user.id}`, {
        headers: {
          'X-User-Id': user.id
        }
      })
      
      if (response.ok) {
        const prefs = await response.json()
        if (prefs.email) setEmail(prefs.email)
        if (prefs.phone) setPhone(prefs.phone)
      }
    } catch (err) {
      console.error('Failed to load preferences:', err)
    }
  }

  const saveAlert = async () => {
    if (!user?.id) {
      setTestStatus('Please sign in to save alerts')
      setTimeout(() => setTestStatus(''), 3000)
      return
    }
    
    if (!email || !alertPreferences.rule_name) {
      setTestStatus('Please enter an email address and rule name')
      setTimeout(() => setTestStatus(''), 3000)
      return
    }
    
    setSaving(true)
    setTestStatus('')
    
    try {
      // First save user preferences
      await fetch(`${ALERTS_API_URL}/alerts/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.id
        },
        body: JSON.stringify({
          user_id: user.id,
          email,
          phone,
          notification_methods: ['email', phone ? 'sms' : null].filter(Boolean),
          email_enabled: true,
          sms_enabled: !!phone,
          browser_enabled: true
        })
      })
      
      // Prepare alert rule
      const alertRule = {
        ...alertPreferences,
        user_id: user.id,
        created_at: Date.now(),
        updated_at: Date.now()
      }
      
      if (editingAlert) {
        // Update existing alert
        const response = await fetch(`${ALERTS_API_URL}/alerts/rules/${editingAlert.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': user.id
          },
          body: JSON.stringify(alertRule)
        })
        
        if (!response.ok) throw new Error('Failed to update alert')
      } else {
        // Create new alert
        const response = await fetch(`${ALERTS_API_URL}/alerts/rules`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': user.id
          },
          body: JSON.stringify(alertRule)
        })
        
        if (!response.ok) throw new Error('Failed to create alert')
      }
      
      // Reload alerts
      await loadAlerts()
      
      setShowCreateForm(false)
      setEditingAlert(null)
      setTestStatus(editingAlert ? 'Alert updated successfully!' : 'Alert created successfully!')
      setTimeout(() => setTestStatus(''), 3000)
    } catch (err) {
      console.error('Save alert error:', err)
      setTestStatus('Failed to save alert')
      setTimeout(() => setTestStatus(''), 3000)
    } finally {
      setSaving(false)
    }
  }

  const deleteAlert = async (alertId: string) => {
    if (!confirm('Are you sure you want to delete this alert?')) return
    
    if (!user?.id) return
    
    try {
      const response = await fetch(`${ALERTS_API_URL}/alerts/rules/${alertId}`, {
        method: 'DELETE',
        headers: {
          'X-User-Id': user.id
        }
      })
      
      if (!response.ok) throw new Error('Failed to delete alert')
      
      await loadAlerts()
      setTestStatus('Alert deleted successfully!')
      setTimeout(() => setTestStatus(''), 3000)
    } catch (err) {
      setTestStatus('Failed to delete alert')
      setTimeout(() => setTestStatus(''), 3000)
    }
  }

  const toggleAlert = async (alertId: string) => {
    if (!user?.id) return
    
    try {
      const alert = alerts.find(a => a.id === alertId)
      if (!alert) return
      
      const response = await fetch(`${ALERTS_API_URL}/alerts/rules/${alertId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.id
        },
        body: JSON.stringify({
          ...alert,
          enabled: !alert.enabled,
          updated_at: Date.now()
        })
      })
      
      if (!response.ok) throw new Error('Failed to toggle alert')
      
      await loadAlerts()
    } catch (err) {
      console.error('Failed to toggle alert:', err)
    }
  }

  const editAlert = (alert: Alert) => {
    setEditingAlert(alert)
    // Note: email/phone would come from user preferences, not the alert rule
    setAlertPreferences({
      rule_name: alert.rule_name || '',
      min_spread: alert.min_spread || 0.05,
      min_volume: alert.min_volume || 10000,
      min_confidence_score: alert.min_confidence_score || 0.2,
      platforms: alert.platforms || ['polymarket', 'kalshi', 'predictit'],
      max_alerts_per_hour: alert.max_alerts_per_hour || 10,
      cooldown_minutes: alert.cooldown_minutes || 30,
      enabled: alert.enabled !== false
    })
    setShowCreateForm(true)
  }

  const sendTestAlert = async (ruleId: string) => {
    if (!user?.id) {
      setTestStatus('Please sign in first')
      setTimeout(() => setTestStatus(''), 3000)
      return
    }
    
    setTestStatus('Sending test alert...')
    
    try {
      const response = await fetch(`${ALERTS_API_URL}/alerts/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': user.id
        },
        body: JSON.stringify({ ruleId })
      })
      
      if (!response.ok) throw new Error('Failed to send test alert')
      
      setTestStatus('Test alert sent! Check your email.')
      setTimeout(() => setTestStatus(''), 5000)
    } catch (err) {
      setTestStatus('Failed to send test alert')
      setTimeout(() => setTestStatus(''), 3000)
    }
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Section Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '1px',
        marginBottom: '1.5rem',
        borderBottom: '1px solid var(--sacred-border)'
      }}>
        <button
          className={`tab ${activeSection === 'manage' ? 'active' : ''}`}
          onClick={() => {
            setActiveSection('manage')
            setShowCreateForm(false)
            setEditingAlert(null)
          }}
          style={{ fontSize: '0.65rem' }}
        >
          MANAGE ALERTS
        </button>
        <button
          className={`tab ${activeSection === 'history' ? 'active' : ''}`}
          onClick={() => setActiveSection('history')}
          style={{ fontSize: '0.65rem' }}
        >
          ALERT HISTORY
        </button>
      </div>

      {activeSection === 'manage' ? (
        showCreateForm ? (
          // Alert Creation/Edit Form
          <div style={{ maxWidth: '600px' }}>
            <h3 style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>
              {editingAlert ? 'EDIT ALERT' : 'CREATE NEW ALERT'}
            </h3>
          
          {!user && (
            <div style={{ 
              padding: '1.5rem',
              background: 'var(--sacred-surface)',
              border: '1px solid var(--sacred-border)',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <h4 style={{ fontSize: '0.7rem', marginBottom: '0.5rem', color: 'var(--sacred-warning)' }}>
                AUTHENTICATION REQUIRED
              </h4>
              <p style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)', marginBottom: '1rem' }}>
                Sign in to save your alert preferences and receive notifications
              </p>
              <button className="action-btn primary" onClick={() => signIn()}>
                SIGN IN TO CONTINUE
              </button>
            </div>
          )}
          
          {/* Alert Details */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ fontSize: '0.7rem', marginBottom: '1rem', color: 'var(--sacred-accent)' }}>
              ALERT DETAILS
            </h4>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
                ALERT NAME *
              </label>
              <input
                type="text"
                value={alertPreferences.rule_name}
                onChange={(e) => setAlertPreferences({
                  ...alertPreferences,
                  rule_name: e.target.value
                })}
                placeholder="My Alert Rule"
                style={{
                  width: '100%',
                  background: 'var(--sacred-surface)',
                  border: '1px solid var(--sacred-border)',
                  color: 'var(--sacred-text)',
                  padding: '0.5rem',
                  fontSize: '0.65rem'
                }}
              />
            </div>
          </div>
          
          {/* Contact Information */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ fontSize: '0.7rem', marginBottom: '1rem', color: 'var(--sacred-accent)' }}>
              CONTACT INFORMATION
            </h4>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
                  EMAIL ADDRESS
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={{
                    width: '100%',
                    background: 'var(--sacred-surface)',
                    border: '1px solid var(--sacred-border)',
                    color: 'var(--sacred-text)',
                    padding: '0.5rem',
                    fontSize: '0.65rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
                  PHONE NUMBER (SMS) - Optional
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  style={{
                    width: '100%',
                    background: 'var(--sacred-surface)',
                    border: '1px solid var(--sacred-border)',
                    color: 'var(--sacred-text)',
                    padding: '0.5rem',
                    fontSize: '0.65rem'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Alert Preferences */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ fontSize: '0.7rem', marginBottom: '1rem', color: 'var(--sacred-accent)' }}>
              ALERT PREFERENCES
            </h4>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
                  MINIMUM SPREAD (%)
                </label>
                <input
                  type="number"
                  value={(alertPreferences.min_spread * 100).toFixed(1)}
                  onChange={(e) => setAlertPreferences({
                    ...alertPreferences,
                    min_spread: (parseFloat(e.target.value) || 0) / 100
                  })}
                  min="0"
                  max="100"
                  step="0.5"
                  style={{
                    width: '100%',
                    background: 'var(--sacred-surface)',
                    border: '1px solid var(--sacred-border)',
                    color: 'var(--sacred-text)',
                    padding: '0.5rem',
                    fontSize: '0.65rem'
                  }}
                />
                <span style={{ fontSize: '0.6rem', color: 'var(--sacred-text-dim)' }}>
                  Alert when arbitrage spread exceeds this percentage
                </span>
              </div>
              
              <div>
                <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
                  MINIMUM VOLUME ($)
                </label>
                <input
                  type="number"
                  value={alertPreferences.min_volume}
                  onChange={(e) => setAlertPreferences({
                    ...alertPreferences,
                    min_volume: parseInt(e.target.value) || 0
                  })}
                  min="0"
                  step="1000"
                  style={{
                    width: '100%',
                    background: 'var(--sacred-surface)',
                    border: '1px solid var(--sacred-border)',
                    color: 'var(--sacred-text)',
                    padding: '0.5rem',
                    fontSize: '0.65rem'
                  }}
                />
                <span style={{ fontSize: '0.6rem', color: 'var(--sacred-text-dim)' }}>
                  Only alert for markets with volume above this amount
                </span>
              </div>
              
              <div>
                <label style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)' }}>
                  MIN CONFIDENCE SCORE (%)
                </label>
                <input
                  type="number"
                  value={(alertPreferences.min_confidence_score * 100).toFixed(0)}
                  onChange={(e) => setAlertPreferences({
                    ...alertPreferences,
                    min_confidence_score: (parseFloat(e.target.value) || 0) / 100
                  })}
                  min="0"
                  max="100"
                  step="1"
                  style={{
                    width: '100%',
                    background: 'var(--sacred-surface)',
                    border: '1px solid var(--sacred-border)',
                    color: 'var(--sacred-text)',
                    padding: '0.5rem',
                    fontSize: '0.65rem'
                  }}
                />
                <span style={{ fontSize: '0.6rem', color: 'var(--sacred-text-dim)' }}>
                  Alert when market price differs from AI prediction by this amount
                </span>
              </div>
            </div>
          </div>

          {/* Platform Selection */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ fontSize: '0.7rem', marginBottom: '1rem', color: 'var(--sacred-accent)' }}>
              PLATFORMS TO MONITOR
            </h4>
            
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              {['polymarket', 'kalshi', 'predictit'].map(platform => (
                <label key={platform} style={{ fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={alertPreferences.platforms.includes(platform)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setAlertPreferences({
                          ...alertPreferences,
                          platforms: [...alertPreferences.platforms, platform]
                        })
                      } else {
                        setAlertPreferences({
                          ...alertPreferences,
                          platforms: alertPreferences.platforms.filter(p => p !== platform)
                        })
                      }
                    }}
                  />
                  {platform.toUpperCase()}
                </label>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '1rem', 
            alignItems: 'center',
            paddingTop: '1rem',
            borderTop: '1px solid var(--sacred-border)'
          }}>
            <button 
              className="action-btn primary"
              onClick={saveAlert}
              disabled={saving || !email}
            >
              {saving ? 'SAVING...' : editingAlert ? 'UPDATE ALERT' : 'CREATE ALERT'}
            </button>
            
            <button 
              className="action-btn"
              onClick={() => {
                setShowCreateForm(false)
                setEditingAlert(null)
              }}
            >
              CANCEL
            </button>
            
            
            {testStatus && (
              <span style={{ 
                fontSize: '0.65rem',
                color: testStatus.includes('success') || testStatus.includes('sent') 
                  ? 'var(--sacred-green)' 
                  : 'var(--sacred-red)'
              }}>
                {testStatus}
              </span>
            )}
          </div>
          
          <div style={{ 
            marginTop: '2rem',
            padding: '1rem',
            background: 'var(--sacred-surface)',
            border: '1px solid var(--sacred-border)',
            fontSize: '0.65rem',
            color: 'var(--sacred-text-dim)'
          }}>
            <strong style={{ color: 'var(--sacred-text)' }}>How Alerts Work:</strong>
            <ul style={{ margin: '0.5rem 0 0 1rem', paddingLeft: '1rem' }}>
              <li>The system continuously monitors all enabled platforms for arbitrage opportunities</li>
              <li>When an opportunity exceeds your configured thresholds, you'll receive an alert</li>
              <li>Email alerts are sent immediately when opportunities are found</li>
              <li>SMS alerts are limited to high-value opportunities (&gt;$50k volume)</li>
              <li>Each opportunity is alerted only once to prevent spam</li>
            </ul>
          </div>
        </div>
      ) : (
        // Alerts List View
        <div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ fontSize: '0.8rem' }}>YOUR ALERTS</h3>
            {user && (
              <button 
                className="action-btn primary"
                onClick={() => setShowCreateForm(true)}
                style={{ fontSize: '0.65rem' }}
              >
                CREATE NEW ALERT
              </button>
            )}
          </div>
          
          {!user ? (
            <div style={{ 
              padding: '3rem',
              textAlign: 'center',
              background: 'var(--sacred-surface)',
              border: '1px solid var(--sacred-border)'
            }}>
              <h4 style={{ fontSize: '0.8rem', marginBottom: '0.5rem', color: 'var(--sacred-warning)' }}>
                AUTHENTICATION REQUIRED
              </h4>
              <p style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)', marginBottom: '1rem' }}>
                Sign in to create and manage your alerts
              </p>
              <button className="action-btn primary" onClick={() => signIn()}>
                SIGN IN TO CONTINUE
              </button>
            </div>
          ) : loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="loading-spinner" />
            </div>
          ) : alerts.length === 0 ? (
            <div style={{ 
              padding: '3rem',
              textAlign: 'center',
              background: 'var(--sacred-surface)',
              border: '1px solid var(--sacred-border)'
            }}>
              <h4 style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                No alerts configured
              </h4>
              <p style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)', marginBottom: '1rem' }}>
                Create your first alert to get notified about arbitrage opportunities
              </p>
              <button 
                className="action-btn primary"
                onClick={() => setShowCreateForm(true)}
              >
                CREATE YOUR FIRST ALERT
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {alerts.map(alert => (
                <div 
                  key={alert.id}
                  style={{ 
                    background: 'var(--sacred-surface)',
                    border: '1px solid var(--sacred-border)',
                    padding: '1rem',
                    opacity: alert.enabled ? 1 : 0.6
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.75rem'
                  }}>
                    <div>
                      <h4 style={{ fontSize: '0.7rem', marginBottom: '0.25rem' }}>
                        {alert.rule_name || 'Unnamed Alert'}
                      </h4>
                      <p style={{ fontSize: '0.6rem', color: 'var(--sacred-text-dim)' }}>
                        ID: {alert.id}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="action-btn small"
                        onClick={() => toggleAlert(alert.id)}
                        style={{ fontSize: '0.55rem' }}
                      >
                        {alert.enabled ? 'DISABLE' : 'ENABLE'}
                      </button>
                      <button
                        className="action-btn small"
                        onClick={() => editAlert(alert)}
                        style={{ fontSize: '0.55rem' }}
                      >
                        EDIT
                      </button>
                      <button
                        className="action-btn small"
                        onClick={() => deleteAlert(alert.id)}
                        style={{ fontSize: '0.55rem', color: 'var(--sacred-error)' }}
                      >
                        DELETE
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '0.5rem',
                    fontSize: '0.6rem',
                    color: 'var(--sacred-text-dim)'
                  }}>
                    <div>
                      <strong>Spread:</strong> ≥{((alert.min_spread || 0) * 100).toFixed(1)}%
                    </div>
                    <div>
                      <strong>Volume:</strong> ≥${(alert.min_volume || 0).toLocaleString()}
                    </div>
                    <div>
                      <strong>Confidence:</strong> ≥{((alert.min_confidence_score || 0) * 100).toFixed(0)}%
                    </div>
                  </div>
                  
                  <div style={{ 
                    marginTop: '0.5rem',
                    fontSize: '0.6rem',
                    color: 'var(--sacred-text-dim)'
                  }}>
                    <strong>Platforms:</strong> {(alert.platforms || [])
                      .map(p => p.toUpperCase())
                      .join(', ') || 'All'}
                  </div>
                  
                  <div style={{ 
                    marginTop: '0.5rem',
                    fontSize: '0.55rem',
                    color: 'var(--sacred-text-dim)'
                  }}>
                    Created: {new Date(alert.created_at || 0).toLocaleDateString()}
                    {alert.enabled && (
                      <span style={{ color: 'var(--sacred-green)', marginLeft: '1rem' }}>
                        ● ACTIVE
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )) : (
        <div>
          <h3 style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>
            RECENT ALERTS
          </h3>
          
          <div style={{ 
            padding: '3rem',
            textAlign: 'center',
            color: 'var(--sacred-text-dim)',
            background: 'var(--sacred-surface)',
            border: '1px solid var(--sacred-border)'
          }}>
            <h4 style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
              No alerts sent yet
            </h4>
            <p style={{ fontSize: '0.65rem' }}>
              Configure your alert preferences to start receiving notifications
            </p>
          </div>
        </div>
      )}
    </div>
  )
}